import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  getUserExpenseSheets,
  getExpenseSheet,
  createExpenseSheet,
  updateExpenseSheet,
  deleteExpenseSheet,
  submitExpenseSheet,
  cancelExpenseSheet,
  reopenExpenseSheet,
  approveExpenseSheet,
  rejectExpenseSheet,
  reimburseExpenseSheet,
  bulkApproveExpenseSheets,
  bulkRejectExpenseSheets,
  bulkDeleteExpenseSheets,
  getPendingExpenseSheets,
  getAllExpenseSheets,
  createReceipt,
  updateReceipt,
  deleteReceipt,
  uploadReceiptFile,
  type CreateExpenseSheetInput,
  type UpdateExpenseSheetInput,
  type CreateReceiptInput,
  type UpdateReceiptInput,
} from '../services/api/expenses';

/**
 * Invalidate the three expense-sheet list queues every mutation hook needs
 * to refresh — the user's own `['expenseSheets']`, the manager pending
 * bucket (`['pendingExpenseSheets']`), and the admin-wide list
 * (`['allExpenseSheets']`). Mirrors the `invalidateTimeOffLists` helper in
 * `useTimeOff.ts`. Per-item (`['expenseSheet', id]`) and
 * `['approvalCounts']` invalidations remain inline at the call sites
 * because they only apply to a subset of mutations.
 */
function invalidateExpenseSheetLists(queryClient: QueryClient): void {
  invalidateMany(queryClient, [['expenseSheets'], ['pendingExpenseSheets'], ['allExpenseSheets']]);
}

/**
 * Receipt mutations (create/update/delete/upload) all touch the same three
 * caches: the parent sheet detail, the user's own list, and the admin-wide
 * list. They never affect the pending queue or approvalCounts (those only
 * move on sheet-level state transitions), so this is a tighter set than
 * `invalidateExpenseSheetLists`.
 */
function invalidateReceiptCaches(queryClient: QueryClient, sheetId: number): void {
  invalidateMany(queryClient, [['expenseSheet', sheetId], ['expenseSheets'], ['allExpenseSheets']]);
}

export function useUserExpenseSheets(filters?: { state?: string; projectId?: number }) {
  return useQuery({
    queryKey: ['expenseSheets', filters],
    queryFn: () => getUserExpenseSheets(filters),
  });
}

export function useExpenseSheet(id: number | undefined) {
  return useQuery({
    queryKey: ['expenseSheet', id],
    queryFn: () => getExpenseSheet(id!),
    enabled: !!id,
  });
}

export function usePendingExpenseSheets() {
  return useQuery({
    queryKey: ['pendingExpenseSheets'],
    queryFn: getPendingExpenseSheets,
  });
}

export function useAllExpenseSheets(adminView?: boolean) {
  return useQuery({
    queryKey: ['allExpenseSheets', { adminView }],
    queryFn: () => getAllExpenseSheets(adminView ? { adminView: true } : undefined),
  });
}

export function useCreateExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseSheetInput) => createExpenseSheet(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseSheets'] });
    },
  });
}

export function useUpdateExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateExpenseSheetInput }) => updateExpenseSheet(id, input),
    onSuccess: (_data, { id }) => {
      // F3-015: an update may flip totals/state and bump the cockpit
      // approval count and admin all-sheets list. Mirror the bulk hooks.
      invalidateMany(queryClient, [
        ['expenseSheets'],
        ['expenseSheet', id],
        ['allExpenseSheets'],
        ['approvalCounts'],
      ]);
    },
  });
}

export function useDeleteExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteExpenseSheet(id),
    onSuccess: () => {
      // F7-009: admin-deleting a pending sheet drops it out of the
      // approval queue. Every OTHER expense mutation in this file
      // (submit / cancel / reopen / approve / reject / reimburse /
      // bulk*) already invalidates ['approvalCounts'] — delete was
      // the lone outlier, leaving the cockpit badge stale.
      invalidateExpenseSheetLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useSubmitExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => submitExpenseSheet(id),
    onSuccess: (_data, id) => {
      // F3-015: submit moves a sheet INTO the pending queue. Without the
      // pending + admin-wide invalidations the manager's pending list +
      // cockpit badge stay stale until refresh.
      invalidateExpenseSheetLists(queryClient);
      invalidateMany(queryClient, [['expenseSheet', id], ['approvalCounts']]);
    },
  });
}

export function useCancelExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cancelExpenseSheet(id),
    onSuccess: (_data, id) => {
      // F3-015: cancel moves a sheet OUT of the pending queue — mirror
      // the same invalidations as submit so the manager view de-stales.
      invalidateExpenseSheetLists(queryClient);
      invalidateMany(queryClient, [['expenseSheet', id], ['approvalCounts']]);
    },
  });
}

export function useReopenExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => reopenExpenseSheet(id),
    onSuccess: (_data, id) => {
      // F3-015: reopen flips state back to draft, removing the sheet from
      // any pending/approved views the admin or manager is looking at.
      invalidateExpenseSheetLists(queryClient);
      invalidateMany(queryClient, [['expenseSheet', id], ['approvalCounts']]);
    },
  });
}

export function useApproveExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => approveExpenseSheet(id),
    onSuccess: (_data, id) => {
      invalidateExpenseSheetLists(queryClient);
      invalidateMany(queryClient, [['expenseSheet', id], ['approvalCounts']]);
    },
  });
}

export function useRejectExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectExpenseSheet(id, reason),
    onSuccess: (_data, { id }) => {
      invalidateExpenseSheetLists(queryClient);
      invalidateMany(queryClient, [['expenseSheet', id], ['approvalCounts']]);
    },
  });
}

export function useReimburseExpenseSheet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => reimburseExpenseSheet(id),
    onSuccess: (_data, id) => {
      // F3-015: reimbursement is a terminal state change that the admin
      // all-sheets list + approvalCounts badge surface. Mirror the bulk
      // hooks' invalidation set.
      invalidateMany(queryClient, [
        ['expenseSheets'],
        ['expenseSheet', id],
        ['allExpenseSheets'],
        ['approvalCounts'],
      ]);
    },
  });
}

export function useBulkApproveExpenseSheets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkApproveExpenseSheets(ids),
    onSuccess: () => {
      invalidateExpenseSheetLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useBulkRejectExpenseSheets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: number[]; reason?: string }) => bulkRejectExpenseSheets(ids, reason),
    onSuccess: () => {
      invalidateExpenseSheetLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

export function useBulkDeleteExpenseSheets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkDeleteExpenseSheets(ids),
    onSuccess: () => {
      invalidateExpenseSheetLists(queryClient);
      // F9-006: bulk-delete drops pending sheets from the manager queue, so
      // the cockpit `approvalCounts` badge must refresh — mirroring the
      // single-delete fix (F7-009) and the bulk-approve/reject siblings.
      queryClient.invalidateQueries({ queryKey: ['approvalCounts'] });
    },
  });
}

// ===== Receipt Hooks =====

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, input }: { sheetId: number; input: CreateReceiptInput }) => createReceipt(sheetId, input),
    onSuccess: (_data, { sheetId }) => {
      invalidateReceiptCaches(queryClient, sheetId);
    },
  });
}

export function useUpdateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, receiptId, input }: { sheetId: number; receiptId: number; input: UpdateReceiptInput }) =>
      updateReceipt(sheetId, receiptId, input),
    onSuccess: (_data, { sheetId }) => {
      invalidateReceiptCaches(queryClient, sheetId);
    },
  });
}

export function useDeleteReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, receiptId }: { sheetId: number; receiptId: number }) =>
      deleteReceipt(sheetId, receiptId),
    onSuccess: (_data, { sheetId }) => {
      invalidateReceiptCaches(queryClient, sheetId);
    },
  });
}

export function useUploadReceiptFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, receiptId, file }: { sheetId: number; receiptId: number; file: File }) =>
      uploadReceiptFile(sheetId, receiptId, file),
    onSuccess: (_data, { sheetId }) => {
      invalidateReceiptCaches(queryClient, sheetId);
    },
  });
}
